import { useEffect, useState } from "react";
import { Row, Col, Card, Typography, Spin } from "antd";
import {
  FiShoppingBag,
  FiCheckCircle,
  FiUsers,
  FiPlay,
  FiUpload,
  FiMessageCircle,
  FiAlertTriangle,
} from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../slice";
import * as selectors from "../selectors";

const { Text, Title } = Typography;

function OrderStageAnalytic({ selectedCommodities, dateRange }) {
  const dispatch = useDispatch();

  const orderCountAnalytic = useSelector(selectors.selectOrderCountAnalytic);

  const [isOrderCountAnalyticLoading, setIsOrderCountAnalyticLoading] =
    useState(true);

  const analyticsData = [
    {
      key: "total",
      title: "Total Orders",
      value: orderCountAnalytic?.totalOrders?.value || "0",
      description: "All orders",
      icon: <FiShoppingBag size={20} color="#595959" />,
      valueColor: "#262626",
      bgColor: "#fafafa",
    },
    {
      key: "pendingRegistration",
      title: "Pending Registration",
      value: orderCountAnalytic?.pendingRegistration?.value || "0",
      description: "Awaiting registration",
      icon: <FiUsers size={20} color="#d46b08" />,
      valueColor: "#d46b08",
      bgColor: "#fff7e6",
    },
    {
      key: "accepted",
      title: "Accepted",
      value: orderCountAnalytic?.accepted?.value || "0",
      description: "Lab accepted orders",
      icon: <FiCheckCircle size={20} color="#1890ff" />,
      valueColor: "#1890ff",
      bgColor: "#e6f7ff",
    },
    {
      key: "underTesting",
      title: "Under Testing",
      value: orderCountAnalytic?.underTesting?.value || "0",
      description: "Currently under testing",
      icon: <FiPlay size={20} color="#722ed1" />,
      valueColor: "#722ed1",
      bgColor: "#f9f0ff",
    },
    {
      key: "reportsUploaded",
      title: "Reports Uploaded",
      value: orderCountAnalytic?.reportsUploaded?.value || "0",
      description: "Reports ready for review",
      icon: <FiUpload size={20} color="#13c2c2" />,
      valueColor: "#13c2c2",
      bgColor: "#e6fffb",
    },
    {
      key: "completed",
      title: "Completed",
      value: orderCountAnalytic?.completed?.value || "0",
      description: "Successfully completed",
      icon: <FiCheckCircle size={20} color="#52c41a" />,
      valueColor: "#52c41a",
      bgColor: "#f6ffed",
    },
    {
      key: "underDispute",
      title: "Under Dispute",
      value: orderCountAnalytic?.underDispute?.value || "0",
      description: "Disputes raised",
      icon: <FiMessageCircle size={20} color="#fa8c16" />,
      valueColor: "#fa8c16",
      bgColor: "#fff2e6",
    },
    {
      key: "rejected",
      title: "Rejected",
      value: orderCountAnalytic?.rejected?.value || "0",
      description: "Rejected orders",
      icon: <FiAlertTriangle size={20} color="#ff4d4f" />,
      valueColor: "#ff4d4f",
      bgColor: "#fff1f0",
    },
  ];

  useEffect(() => {
    setIsOrderCountAnalyticLoading(true);
    dispatch(
      actions.getOrderCountAnalytic({
        selectedCommodities,
        dateRange,
        onSuccess: () => {
          setIsOrderCountAnalyticLoading(false);
        },
        onFailure: () => {
          setIsOrderCountAnalyticLoading(false);
        },
      })
    );
  }, [dispatch, selectedCommodities, dateRange]);

  return (
    <Row gutter={[12, 12]} style={{ marginBottom: 24 }}>
      {analyticsData.map((item) => (
        <Col
          key={item.key}
          style={{
            flex: "1 1 calc(25% - 12px)",
          }}
        >
          <Card
            style={{
              borderRadius: "12px",
              border: "1px solid #e8e8e8",
              backgroundColor: item.bgColor || "#ffffff",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
              cursor: "pointer",
              transition: "all 0.3s ease",
              transform: "scale(1)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.02) translateY(-6px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0, 0, 0, 0.15)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1) translateY(0px)";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.04)";
            }}
            bodyStyle={{
              padding: "20px",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <Title
                level={5}
                style={{
                  margin: 0,
                  fontWeight: 500,
                  lineHeight: "18px",
                }}
              >
                {item.title}
              </Title>
              <div
                style={{
                  padding: "6px",
                  borderRadius: "8px",
                  backgroundColor: "transparent",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </div>
            </div>

            <div>
              {isOrderCountAnalyticLoading ? (
                <div
                  style={{
                    margin: "4px",
                  }}
                >
                  <Spin />
                </div>
              ) : (
                <Title
                  level={1}
                  style={{
                    margin: 0,
                    fontSize: "28px",
                    fontWeight: 700,
                    color: item.valueColor,
                    lineHeight: "32px",
                    marginBottom: "4px",
                  }}
                >
                  {item.value}
                </Title>
              )}
              <Text
                style={{
                  fontSize: "12px",
                  color: "#999898ff",
                  lineHeight: "14px",
                  fontWeight: 400,
                }}
              >
                {item.description}
              </Text>
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

export default OrderStageAnalytic;
